import { useCallback, useEffect, useRef, useState } from "react";
import { AKTEN_TABS } from "./views/KundenAkte.js";
import type { AktenTabId } from "./views/KundenAkte.js";
import { VERWALTUNGS_BEREICHE } from "./views/Verwaltung.js";
import type { VerwaltungsBereich } from "./views/Verwaltung.js";
import { EINSTELLUNGS_GRUPPEN } from "./views/Einstellungen.js";
import type { EinstellungsGruppe } from "./views/Einstellungen.js";
import { ANGEBOTS_FILTER, AUFTRAGS_FILTER, RECHNUNGS_FILTER,
         einzelfilterAusSegment, filterAusSegment,
         segmentFuerFilter } from "./bereichsfilter.js";
import type { AngebotsFilter, AuftragsFilter, RechnungsFilter } from "./bereichsfilter.js";

/**
 * Adressierbare Bildschirme (Aufgabe 15). Adressiert wird ausschließlich über die
 * Kennung (`id`), nie über die Kundennummer: Die Nummer gibt es je Unternehmen mehrfach
 * und sie ist von Hand änderbar — ein Lesezeichen muss beides überleben.
 *
 * Der Bearbeiten-Modus bekommt bewusst KEINE eigene Adresse: Ein Lesezeichen auf ein
 * offenes Formular wäre sinnlos, und die Zurück-Taste hinterließe eine gehaltene Sperre.
 */
/**
 * Die GLOBALE LISTE, aus der ein Ziel betreten wurde (Befund iPhone-Rückweg, 05.09.2026).
 *
 * Angebote, Aufträge und der Artikelkatalog sind Sucheinstiege: Man springt von dort in ein
 * FREMDES Ziel — in die Kundenakte auf einen Vorgang, aus dem Katalog in die Artikelpflege
 * der Verwaltung. Auf dem iPhone (Ein-Spalten-Layout) führte der Zurück-Weg dort bis dahin
 * pauschal zur KUNDENliste, weil der Rahmen nur diesen einen Rückweg kannte. Wer aus der
 * Angebotssuche kam, landete damit an einem Ort, an dem er nie war.
 *
 * Die Herkunft reist deshalb — wie `vorgangId` — am NAVIGATIONSZIEL mit und steht NICHT in
 * der Adresse: Sie ist kein Bildschirm, sondern der Weg dorthin. Ein Lesezeichen oder ein
 * Neuladen kennt sie folglich nicht; dann gilt wieder der Ursprung, den die Adresse selbst
 * hergibt (die Kundenliste zur Akte, kein Rückweg zur Verwaltung). Das ist die richtige
 * Richtung: Ein erfundener Ursprung wäre schlimmer als gar keiner.
 */
export type Herkunft = "angebote" | "auftraege" | "artikel" | "rechnungen";

export type Route =
  | { name: "kundenliste" }
  | { name: "kundeNeu" }
  /**
   * Ein Kunde auf einem Reiter seiner Akte.
   *
   * `vorgangId` ist die EINZIGE Angabe hier, die nicht in der Adresse landet: Sie sagt dem
   * Reiter, welchen Vorgang er nach dem Sprung aus einer globalen Liste (`/angebote`,
   * `/auftraege`) gleich aufschlagen soll. Sie reist am NAVIGATIONSZIEL mit — dem einzigen
   * Weg, auf dem in dieser Anwendung ein Bildschirmwechsel stattfindet — und braucht damit
   * keinen zweiten Zustand neben der Adresse, der mit ihr auseinanderlaufen könnte
   * (dieselbe Regel wie beim hervorgehobenen Navigationspunkt in App.tsx).
   *
   * Warum sie NICHT adressierbar ist: Ein aufgeschlagener Vorgang ist ein Bearbeitungs-
   * zustand, kein Bildschirm — genau wie der Bearbeiten-Modus unten. Ein Lesezeichen
   * darauf verspräche einen Stand, den es beim nächsten Öffnen so nicht mehr gibt. Genau
   * deshalb ist das Verfallen richtig herum eingebaut: Neuladen und Zurück-Taste gehen
   * über `parseRoute`, und die kennt das Feld gar nicht — der Reiter steht dann schlicht
   * mit seiner Liste da.
   */
  | { name: "kunde"; id: string; tab: AktenTabId; vorgangId?: string; herkunft?: Herkunft }
  /**
   * Die persönlichen Einstellungen (Bereichs-Spalten, Etappe 3).
   *
   * `gruppe` ist OPTIONAL und sein Fehlen ist die ÜBERSICHT — dieselbe Regel wie bei
   * `bereich` der Verwaltung weiter unten: Seit die Gruppen als Untermenü in der SPALTE
   * stehen, sind „Übersicht" und „offene Gruppe" derselbe Bildschirm in zwei Zuständen.
   * `/einstellungen` zeigt das Untermenü ohne Auswahl, `/einstellungen/<gruppe>` denselben
   * Bildschirm mit offener Gruppe. Auf dem iPhone ist genau das die Faltung (siehe
   * spalteZuerst in App.tsx) — ohne die gruppenlose Adresse gäbe es dort keinen Weg zum
   * Untermenü zurück. Und alte Lesezeichen auf `/einstellungen` bleiben gültig.
   */
  | { name: "einstellungen"; gruppe?: EinstellungsGruppe }
  /**
   * Die drei OBJEKTBEREICHE Angebote, Aufträge und Artikel.
   *
   * Bis zur Etappe „Bereichs-Spalten" (05.09.2026) waren das reine SUCHEINSTIEGE ohne
   * Unterseiten: Man sprang von der globalen Liste in ein fremdes Ziel (die Kundenakte,
   * die Artikelpflege), und der Bereich selbst hatte keinen Detailort. Seither arbeiten
   * sie wie der Kundenbereich — Liste links, Detail rechts —, und damit ist das geöffnete
   * Objekt ein eigener Bildschirm und gehört in die Adresse: `/angebote/<id>`.
   *
   * `id` ist deshalb OPTIONAL und nicht ein zweiter Adressname: Liste und geöffnetes
   * Objekt sind derselbe Bildschirm in zwei Zuständen (genau wie beim Kunden, wo die
   * Übersicht ohne Reiter in der Adresse steht). Der reine Listenpfad `/angebote` bleibt
   * damit gültig — alte Lesezeichen und die Hauptnavigation treffen weiter.
   *
   * Adressiert wird auch hier ausschliesslich über die Kennung, nie über die Angebots-
   * oder Auftragsnummer: Die Nummer ist von Hand änderbar, ein Lesezeichen muss das
   * überleben (dieselbe Begründung wie beim Kunden oben).
   *
   * Seit dem Paket „Filterkopf statt zweiter Spalte" (Entscheid Dirk 07.09.2026) trägt
   * die Adresse von Angebot und Auftrag AUSSERDEM den Statusfilter — dieselbe Stufe, die
   * der Rechnungs-Bereich seit dem 06.09. hat, und aus demselben Grund: Seit die Filterung
   * als Chip-Leiste im Filterkopf steht, IST „die abgelehnten Angebote" ein Bildschirm,
   * und ein Lesezeichen darf darauf zeigen. Er steht als PFADSEGMENT vor der Kennung
   * (`/angebote/abgelehnt/<id>`) und nicht als Abfrageparameter: Diese Anwendung adressiert
   * ausschliesslich über den Pfad (`parseRoute` sieht `location.search` gar nicht), und ein
   * `?status=` wäre der erste und einzige Zustand, den ein Lesezeichen nur zur Hälfte
   * träfe.
   *
   * `filter` trägt GENAU EINEN Namen, weil der Server-Weg dieser beiden Bereiche genau
   * einen Status nimmt (`VorgangsSucheParams.status`, siehe api-client) — die Chip-Leiste
   * ist dort einfach wählbar (siehe components/Filterkopf.tsx). Sein Fehlen heisst „alle",
   * dieselbe Regel wie bei `bereich` der Verwaltung (`/verwaltung` IST die Übersicht).
   *
   * Der ARTIKEL bleibt ohne Filtersegment: Er hat keinen Status, und seine beiden Filter
   * (Unternehmen, Kategorie) taugen nicht für die Adresse — die Kategorien kommen zur
   * LAUFZEIT vom Server, ein Segment aus einer Menge, die der Router nicht kennt, liesse
   * sich weder prüfen noch von einer Objektkennung unterscheiden (siehe bereichsfilter.ts).
   *
   * NICHT in der Adresse stehen — bewusst — Suchtext, Blätterung und die Zusatzfilter des
   * Filterkopfs: Ein Lesezeichen auf „Seite 7 bei Suchtext X" ist morgen eine andere
   * Seite 7.
   */
  | { name: "angebote"; filter?: AngebotsFilter; id?: string; herkunft?: Herkunft }
  | { name: "auftraege"; filter?: AuftragsFilter; id?: string; herkunft?: Herkunft }
  | { name: "artikel"; id?: string; herkunft?: Herkunft }
  /** Die TICKETS (Spec 2026-10-18): Board oder Liste, wahlweise mit geöffnetem Ticket —
   *  `/tickets/<id>` öffnet den Arbeitsdialog über dem Board (Direktaufruf). `/tickets/board`
   *  wird als Board gelesen, geschrieben wird es als `/tickets` (das IST das Board). */
  | { name: "tickets"; ansicht?: "liste"; id?: string }
  /**
   * Der RECHNUNGS-Bereich — wie die drei darüber, PLUS einer Stufe: dem STATUS-FILTER
   * (Paket „Rechnungsstatus", Entscheid Dirk 06.09.2026).
   *
   * Seit die Spalte keine Kundensuche mehr führt, sondern eine Liste vorgefertigter
   * Filterungen (Alle Rechnungen, Entwurf, Festgeschrieben, Bezahlt, Storniert), IST der
   * gewählte Filter ein Bildschirm — genau wie der gewählte Bereich der Verwaltung. Er
   * gehört deshalb in die Adresse, und zwar als PFADSEGMENT und nicht als
   * Abfrageparameter: Diese Anwendung adressiert ausschliesslich über den Pfad
   * (`parseRoute` sieht `location.search` gar nicht), und ein `?status=` wäre der erste
   * und einzige Zustand, den ein Lesezeichen nur zur Hälfte träfe.
   *
   * `filter` ist OPTIONAL, und sein Fehlen ist der oberste Eintrag „Alle Rechnungen" —
   * dieselbe Regel wie bei `bereich` der Verwaltung (`/verwaltung` IST die Übersicht).
   * Damit bleibt `/rechnungen/<id>` gültig: Ein Lesezeichen aus der Zeit vor diesem Paket
   * öffnet den Beleg über „Alle Rechnungen", und die Naht aus dem Auftrags-Bereich
   * (`/rechnungen/<neueId>`) braucht keinen Filter zu erfinden.
   *
   * Seit dem Paket „Filterkopf statt zweiter Spalte" (Entscheid Dirk 07.09.2026) ist
   * `filter` eine LISTE: Die Filterliste der Spalte und die Chip-Leiste darunter waren
   * dieselben vier Gruppen in zwei Bedienelementen und sind zu EINER Chip-Leiste im
   * Filterkopf verschmolzen. Sie ist mehrfach wählbar (der Server-Weg nimmt den Status als
   * wiederholbaren Parameter, siehe `searchInvoices`), also trägt das Segment mehrere
   * Namen durch Komma getrennt: `/rechnungen/entwurf,bezahlt`. Das Bestandssegment mit
   * EINEM Namen (`/rechnungen/bezahlt`) bleibt damit gültig und bedeutet unverändert
   * dasselbe. Das reservierte Wort `keine` trägt den einen Zustand, den die Leiste sonst
   * nicht adressieren könnte — kein Status eingeschaltet.
   *
   * NICHT in der Adresse stehen — bewusst — Suchtext und Blätterung: Ein Lesezeichen auf
   * „Seite 7 bei Suchtext X" ist morgen eine andere Seite 7.
   *
   * Ein Unterschied zu den Schwestern bleibt und steht im Kopf von
   * views/BereichRechnungen.tsx: Die Adresse `/rechnungen/<id>` allein genügt nicht zum
   * Lesen des Belegs — die API kennt nur den Weg über den Kunden. Das ist kein Grund, die
   * Adresse anders zu schneiden: Ein Lesezeichen darf auf den Beleg zeigen, und was ihm
   * fehlt, sagt der Bereich.
   */
  | { name: "rechnungen"; filter?: readonly RechnungsFilter[]; id?: string;
      herkunft?: Herkunft }
  /**
   * Der Dienste-Status (Paket „Menü komplett") — seit Etappe 3 der Bereichs-Spalten mit
   * einer Detailebene: `/schnittstellen/<dienst>`.
   *
   * `dienst` ist die KENNUNG des Dienstes, kleingeschrieben — nicht sein Anzeigename. Die
   * Dienste haben stabile Kennungen: `schluessel` gehört zum Vertrag der Statusroute und
   * wird dort als Konstante vergeben (`RENDER_ENGINE`, siehe
   * api/src/routes/schnittstellen.ts). Ein Lesezeichen überlebt damit eine Umbenennung der
   * Zeile — dieselbe Absicht wie bei der Kennung statt der Kundennummer ganz oben.
   *
   * Sie ist ein `string` und kein aufgezählter Typ, denn die Menge der Dienste steht ERST
   * ZUR LAUFZEIT fest: Sie kommt vom Server (heute eine Zeile, mit den Adaptern für DATEV
   * und Portal-Abgleich mehr). Der Router prüft deshalb nur die GESTALT (siehe
   * DIENST_MUSTER) und nicht die Zugehörigkeit; ob es den Dienst gibt, sagt die Ansicht,
   * sobald sie gemessen hat — genauso wie `/rechnungen/<id>` auf einen Beleg zeigen darf,
   * den erst der Bereich auflöst.
   */
  | { name: "schnittstellen"; dienst?: string }
  /**
   * Der Verwaltungsbereich (Paket 2b, Aufgabe 11). Der Bereich steht in der Adresse,
   * damit ein Lesezeichen auf „Nummernkreise" auch dort landet — genau wie beim Tab
   * der Kundenakte. Wer die Adresse ohne ADMIN-Rolle öffnet, wird in App.tsx auf die
   * Kundenliste umgeleitet; die Adresse selbst kennt keine Rollen.
   *
   * `bereich` ist seit Etappe 2, Aufgabe 1 OPTIONAL — aus demselben Grund wie `id` bei
   * den vier Objektbereichen darüber: Seit die Bereiche als Untermenü in der SPALTE
   * stehen, sind „Übersicht" und „offener Bereich" derselbe Bildschirm in zwei
   * Zuständen. `/verwaltung` zeigt das Untermenü ohne Auswahl, `/verwaltung/<bereich>`
   * denselben Bildschirm mit offenem Bereich. Auf dem iPhone ist genau das die Faltung:
   * ohne Bereich die Spalte, mit Bereich sein Inhalt (siehe spalteZuerst in App.tsx) —
   * ohne die bereichlose Adresse gäbe es dort keinen Weg zum Untermenü zurück.
   *
   * `id` kam mit Etappe 2, Aufgabe 2 hinzu: das geöffnete OBJEKT eines Bereichs
   * (`/verwaltung/unternehmen/<id>`). Dieselbe Sache wie `id` bei den vier Objektbereichen
   * darüber, nur eine Ebene tiefer — die Verwaltung schaltet ihre Bereiche vor.
   *
   * Die Kennung steht hier GENERISCH und nicht nur für die Bereiche mit Objektliste: Ob ein
   * Bereich eine zweite Ebene hat, sagt `VERWALTUNGS_BEREICHE` (Feld `objektliste`), und
   * ausgewertet wird es dort, wo der Bereich gezeichnet wird. Ein Router, der dieselbe
   * Tatsache ein zweites Mal wüsste, liefe beim nächsten Bereich mit Objektliste
   * auseinander — und die Tabelle soll die EINE Quelle bleiben.
   */
  | { name: "verwaltung"; bereich?: VerwaltungsBereich; id?: string; herkunft?: Herkunft }
  | { name: "unbekannt"; pfad: string };

/**
 * Nur eine echte UUID gilt als Kennung. Ohne diese Prüfung würde /kunden/irgendwas als
 * Kunde durchgehen und erst die API mit 404 antworten — so ist die Adresse selbst schon
 * als unbekannt erkennbar (und /kunden/neu bleibt eindeutig das Anlegen).
 */
const UUID_MUSTER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Die zulässige GESTALT einer Dienst-Kennung in der Adresse (Etappe 3).
 *
 * Anders als bei allen anderen Segmenten kann der Router hier nicht auf Zugehörigkeit
 * prüfen — welche Dienste es gibt, weiss erst die Antwort des Servers (siehe die Notiz am
 * Adresstyp oben). Geprüft wird deshalb nur, dass das Segment überhaupt wie eine Kennung
 * aussieht: Buchstaben, Ziffern, Unterstrich, Bindestrich. Ein Segment mit Punkten,
 * Leerzeichen oder Schrägstrichen wäre keine Kennung, sondern ein Vertipper — und der
 * gehört wie überall auf „unbekannt".
 */
const DIENST_MUSTER = /^[a-z0-9_-]+$/;

function istAktenTab(wert: string): wert is AktenTabId {
  return AKTEN_TABS.some((t) => t.id === wert);
}

function istVerwaltungsBereich(wert: string): wert is VerwaltungsBereich {
  return VERWALTUNGS_BEREICHE.some((b) => b.id === wert);
}

function istEinstellungsGruppe(wert: string): wert is EinstellungsGruppe {
  return EINSTELLUNGS_GRUPPEN.some((g) => g.id === wert);
}

export function parseRoute(pfad: string): Route {
  // Abschließende Schrägstriche übergehen: /kunden/ meint /kunden.
  const teile = pfad.split("/").filter((t) => t !== "");

  if (teile.length === 0) return { name: "kundenliste" };

  const [kopf, zweit, dritt] = teile;

  if (kopf === "kunden") {
    if (teile.length === 1) return { name: "kundenliste" };
    if (zweit === "neu" && teile.length === 2) return { name: "kundeNeu" };
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length <= 3) {
      // Unbekannter Tab fällt auf die Übersicht zurück, statt leer zu bleiben — ein
      // veraltetes Lesezeichen auf einen umbenannten Tab soll den Kunden trotzdem öffnen.
      const tab: AktenTabId =
        dritt !== undefined && istAktenTab(dritt) ? dritt : "uebersicht";
      return { name: "kunde", id: zweit.toLowerCase(), tab };
    }
    return { name: "unbekannt", pfad };
  }

  if (kopf === "verwaltung" && teile.length <= 3) {
    // Kein oder ein unbekannter Bereich führt auf die ÜBERSICHT — den Bildschirm, auf dem
    // das Untermenü ohne Auswahl steht. Dieselbe Absicht wie beim veralteten Lesezeichen
    // auf einen umbenannten Reiter (es soll etwas Brauchbares aufmachen), nur mit dem
    // besseren Ziel: Die Übersicht NENNT alle Bereiche, statt einen davon herauszugreifen.
    if (zweit === undefined || !istVerwaltungsBereich(zweit)) return { name: "verwaltung" };
    // Dieselbe UUID-Prüfung wie überall (siehe UUID_MUSTER). Eine unbrauchbare Kennung
    // führt hier aber NICHT auf „unbekannt" wie bei /angebote/irgendwas: Der Bereich davor
    // ist gültig und für sich schon ein brauchbarer Bildschirm — dieselbe wohlwollende
    // Richtung wie beim unbekannten Bereich darüber.
    if (dritt !== undefined && UUID_MUSTER.test(dritt)) {
      return { name: "verwaltung", bereich: zweit, id: dritt.toLowerCase() };
    }
    return { name: "verwaltung", bereich: zweit };
  }

  /*
   * Der RECHNUNGS-Bereich hat seit dem Paket „Rechnungsstatus" eine Stufe MEHR als seine
   * drei Schwestern (den Statusfilter) und steht deshalb nicht mehr in deren gemeinsamer
   * Regel. Vier Gestalten, und die Reihenfolge der Zweige ist die Aussage:
   *
   *   /rechnungen                    → alle Rechnungen, nichts geöffnet
   *   /rechnungen/<kennung>          → alle Rechnungen, ein Beleg geöffnet (BESTANDSADRESSE
   *                                     aus der Zeit vor diesem Paket — sie bleibt gültig)
   *   /rechnungen/<filter>           → eine Statusliste
   *   /rechnungen/<filter>/<kennung> → eine Statusliste mit geöffnetem Beleg
   *
   * Die Kennung wird ZUERST geprüft: Sie ist eine UUID und kann mit keinem der vier
   * Filternamen zusammenfallen — die Reihenfolge ist also nicht die Auflösung einer
   * Zweideutigkeit, sondern nur die kürzere Schreibweise.
   *
   * Ein UNBEKANNTES Segment führt auf „unbekannt" und nicht wohlwollend auf die Liste
   * (anders als bei der Verwaltung, wo ein umbenannter Bereich denkbar ist): Die vier
   * Filternamen sind eine geschlossene, kleine Menge, ein anderer Wert ist ein Vertipper —
   * und dieselbe Zusage stand hier schon vor diesem Paket (test/router.test.ts).
   */
  if (kopf === "rechnungen") {
    if (teile.length === 1) return { name: "rechnungen" };
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length === 2) {
      return { name: "rechnungen", id: zweit.toLowerCase() };
    }
    if (zweit !== undefined) {
      const filter = filterAusSegment(zweit, RECHNUNGS_FILTER);
      if (filter !== null) {
        // ALLE vier aufgezählt heisst „kein Filter": Die Adresse dafür ist /rechnungen,
        // und zwei Gestalten für denselben Bildschirm wären eine zu viel.
        const anteil = filter.length >= RECHNUNGS_FILTER.length ? {} : { filter };
        if (teile.length === 2) return { name: "rechnungen", ...anteil };
        if (dritt !== undefined && UUID_MUSTER.test(dritt) && teile.length === 3) {
          return { name: "rechnungen", ...anteil, id: dritt.toLowerCase() };
        }
      }
    }
    return { name: "unbekannt", pfad };
  }

  /*
   * ANGEBOTE und AUFTRÄGE haben seit dem 07.09.2026 dieselben vier Gestalten wie die
   * Rechnungen darüber, nur mit einem einfachen Filternamen (siehe den Typ oben):
   *
   *   /angebote                      → alle Angebote, nichts geöffnet
   *   /angebote/<kennung>            → alle Angebote, eines geöffnet (BESTANDSADRESSE)
   *   /angebote/<filter>             → eine Statusliste
   *   /angebote/<filter>/<kennung>   → eine Statusliste mit geöffnetem Angebot
   *
   * Die Kennung wird ZUERST geprüft: Sie ist eine UUID und kann mit keinem Filternamen
   * zusammenfallen — die Reihenfolge ist also nicht die Auflösung einer Zweideutigkeit,
   * sondern nur die kürzere Schreibweise.
   */
  /*
   * Die zwei Zweige stehen AUSGESCHRIEBEN nebeneinander und nicht in einer gemeinsamen
   * Regel wie vor diesem Paket: `AngebotsFilter` und `AuftragsFilter` sind verschiedene
   * Mengen, und erst NACH der Verengung auf einen Adressnamen weiss der Typprüfer, welche
   * von beiden gemeint ist. Eine gemeinsame Zeile bräuchte eine Zusicherung (`as`) — und
   * die liesse „aktiv" als Angebotsfilter durchgehen, also genau den Fehler, gegen den die
   * geschlossenen Mengen da sind. Die zehn doppelten Zeilen sind der günstigere Preis.
   */
  if (kopf === "angebote") {
    if (teile.length === 1) return { name: "angebote" };
    // Dieselbe UUID-Prüfung wie beim Kunden: Ohne sie ginge /angebote/irgendwas als
    // Objekt durch und erst die API antwortete mit 404.
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length === 2) {
      return { name: "angebote", id: zweit.toLowerCase() };
    }
    const filter = zweit === undefined
      ? null : einzelfilterAusSegment(zweit, ANGEBOTS_FILTER);
    if (filter !== null) {
      if (teile.length === 2) return { name: "angebote", filter };
      if (dritt !== undefined && UUID_MUSTER.test(dritt) && teile.length === 3) {
        return { name: "angebote", filter, id: dritt.toLowerCase() };
      }
    }
    return { name: "unbekannt", pfad };
  }

  if (kopf === "auftraege") {
    if (teile.length === 1) return { name: "auftraege" };
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length === 2) {
      return { name: "auftraege", id: zweit.toLowerCase() };
    }
    const filter = zweit === undefined
      ? null : einzelfilterAusSegment(zweit, AUFTRAGS_FILTER);
    if (filter !== null) {
      if (teile.length === 2) return { name: "auftraege", filter };
      if (dritt !== undefined && UUID_MUSTER.test(dritt) && teile.length === 3) {
        return { name: "auftraege", filter, id: dritt.toLowerCase() };
      }
    }
    return { name: "unbekannt", pfad };
  }

  // Der ARTIKEL-Bereich bleibt bei den zwei Gestalten, die er seit der Etappe
  // „Bereichs-Spalten" hat — er trägt keinen Statusfilter (siehe den Typ oben).
  if (kopf === "artikel") {
    if (teile.length === 1) return { name: kopf };
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length === 2) {
      return { name: kopf, id: zweit.toLowerCase() };
    }
    return { name: "unbekannt", pfad };
  }

  // Die TICKETS: Board (ohne Segment, wahlweise `board`) oder Liste, darunter höchstens eine
  // Ticket-Kennung. Alles andere — etwa `/tickets/raster` — ist keine Adresse: Die Darstellung
  // je Spalte/Raster merkt sich der Browser, nicht die Adresse (F19).
  if (kopf === "tickets") {
    if (teile.length === 1) return { name: "tickets" };
    if ((zweit === "liste" || zweit === "board") && teile.length === 2) {
      return zweit === "liste" ? { name: "tickets", ansicht: "liste" } : { name: "tickets" };
    }
    if (zweit !== undefined && UUID_MUSTER.test(zweit) && teile.length === 2) {
      return { name: "tickets", id: zweit.toLowerCase() };
    }
    if ((zweit === "liste" || zweit === "board") && dritt !== undefined && UUID_MUSTER.test(dritt)
        && teile.length === 3) {
      return zweit === "liste"
        ? { name: "tickets", ansicht: "liste", id: dritt.toLowerCase() }
        : { name: "tickets", id: dritt.toLowerCase() };
    }
    return { name: "unbekannt", pfad };
  }

  if (kopf === "einstellungen" && teile.length <= 2) {
    // Keine oder eine unbekannte Gruppe führt auf die ÜBERSICHT — den Bildschirm, auf dem
    // das Untermenü ohne Auswahl steht. Dieselbe wohlwollende Richtung wie bei der
    // Verwaltung oben, und aus demselben Grund: Die Übersicht NENNT alle Gruppen, statt
    // eine davon herauszugreifen, und ein Lesezeichen auf eine umbenannte Gruppe soll die
    // Einstellungen trotzdem aufmachen.
    if (zweit === undefined || !istEinstellungsGruppe(zweit)) return { name: "einstellungen" };
    return { name: "einstellungen", gruppe: zweit };
  }

  if (kopf === "schnittstellen" && teile.length <= 2) {
    if (teile.length === 1) return { name: "schnittstellen" };
    // Kleingeschrieben wie die Objekt-Kennungen oben: Adressen dieser Anwendung sind
    // durchweg klein, und die Ansicht vergleicht gegen dieselbe Schreibweise (dienstPfad
    // in views/Schnittstellen.tsx).
    const dienst = zweit === undefined ? "" : zweit.toLowerCase();
    if (DIENST_MUSTER.test(dienst)) return { name: "schnittstellen", dienst };
    return { name: "unbekannt", pfad };
  }

  return { name: "unbekannt", pfad };
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case "kundenliste":
      return "/kunden";
    case "kundeNeu":
      return "/kunden/neu";
    case "kunde":
      // Der Übersichts-Tab bleibt in der Adresse weg: /kunden/<id> IST die Übersicht.
      // `vorgangId` bleibt ebenfalls weg — absichtlich, siehe die Notiz am Typ oben.
      return route.tab === "uebersicht"
        ? `/kunden/${route.id}`
        : `/kunden/${route.id}/${route.tab}`;
    case "artikel":
      // Ohne gewähltes Objekt bleibt es beim Listenpfad — /artikel IST die Liste,
      // genau wie /kunden/<id> die Übersicht IST. `herkunft` bleibt wie bei der Akte
      // aus der Adresse heraus (siehe die Notiz am Typ oben).
      return route.id === undefined ? `/${route.name}` : `/${route.name}/${route.id}`;
    case "angebote":
    case "auftraege":
    case "rechnungen": {
      /*
       * Dieselbe Regel, eine Stufe tiefer: Ohne Filter bleibt es beim Listenpfad (der IST
       * „alle"), ohne Objekt beim Filterpfad. Ein Objekt OHNE Filter behält seine
       * Bestandsadresse /angebote/<id> — sonst zeigten alte Lesezeichen und die Naht aus
       * dem Auftrag auf einen Pfad, den es nicht gibt.
       *
       * Der Rechnungsfilter ist eine LISTE und wird über den Kodierer geschrieben
       * (bereichsfilter.ts); „alle eingeschaltet" ergibt dort bewusst kein Segment.
       */
      const segment = route.name === "rechnungen"
        ? (route.filter === undefined
            ? undefined : segmentFuerFilter(route.filter, RECHNUNGS_FILTER))
        : route.filter;
      const basis = segment === undefined
        ? `/${route.name}` : `/${route.name}/${segment}`;
      return route.id === undefined ? basis : `${basis}/${route.id}`;
    }
    case "tickets":
      return `/tickets${route.ansicht === "liste" ? "/liste" : ""}${route.id === undefined ? "" : `/${route.id}`}`;
    case "einstellungen":
      // Ohne gewählte Gruppe bleibt es beim Übersichtspfad — /einstellungen IST das
      // Untermenü ohne Auswahl, genau wie /verwaltung (siehe die Notiz am Typ oben).
      return route.gruppe === undefined
        ? "/einstellungen" : `/einstellungen/${route.gruppe}`;
    case "schnittstellen":
      // Dieselbe Regel: /schnittstellen IST die Dienstliste ohne geöffneten Dienst.
      return route.dienst === undefined
        ? "/schnittstellen" : `/schnittstellen/${route.dienst}`;
    case "verwaltung":
      // Ohne gewählten Bereich bleibt es beim Übersichtspfad — /verwaltung IST das
      // Untermenü ohne Auswahl, genau wie /angebote die Angebotsliste IST; und ohne
      // gewähltes Objekt beim Bereichspfad. Eine Kennung OHNE Bereich gibt es nicht: Sie
      // hinge an nichts, deshalb fällt sie hier zusammen mit dem Bereich weg. `herkunft`
      // bleibt wie überall aus der Adresse heraus (siehe die Notiz am Typ oben).
      if (route.bereich === undefined) return "/verwaltung";
      return route.id === undefined
        ? `/verwaltung/${route.bereich}`
        : `/verwaltung/${route.bereich}/${route.id}`;
    case "unbekannt":
      return route.pfad;
  }
}

/**
 * Verlassen-Wächter (Zwischenreview-Fund N3): Ein Bildschirm mit ungespeicherten
 * Änderungen registriert eine Frage („Änderungen verwerfen?"). Sie wird bei JEDER
 * Navigation gestellt — programmatisch wie per Zurück-Taste. Antwortet sie mit false,
 * bleibt die Anwendung stehen. Kein Wächter registriert = normale Navigation.
 */
export type VerlassenWaechter = () => boolean | Promise<boolean>;

/**
 * Jeder Verlaufseintrag der Anwendung trägt seine laufende Nummer im history.state.
 * Nur damit lässt sich bei popstate die Sprungweite bestimmen (die Zurück-Taste kann
 * per langem Druck auch mehrere Einträge auf einmal springen) — und nur mit der
 * Sprungweite lässt sich der Sprung exakt wieder rückgängig machen (siehe useRoute).
 */
function leseEintragsNummer(state: unknown): number | null {
  if (typeof state === "object" && state !== null && "kiEintrag" in state) {
    const wert = (state as { kiEintrag: unknown }).kiEintrag;
    if (typeof wert === "number" && Number.isInteger(wert)) return wert;
  }
  return null;
}

/**
 * Verbindet den Browserverlauf mit der Anwendung. Gedacht für genau EINE Instanz (in
 * App.tsx) — der Wächter lebt in der Instanz, Bildschirme bekommen die Registrierung
 * von dort gereicht.
 *
 * Zur Zurück-Taste mit Wächter: `popstate` ist nicht abbrechbar — wenn das Ereignis
 * eintrifft, steht der Verlaufszeiger schon auf dem Ziel. Der Ausweg: erst den Sprung
 * exakt rückgängig machen (history.go(-delta), aus den Eintragsnummern berechnet),
 * DANN den Wächter fragen. go() verschiebt nur den Zeiger und verändert den Verlauf
 * selbst nicht — bei „Nein" steht danach alles wie vor dem Tastendruck (auch die
 * Vorwärts-Taste funktioniert weiter), bei „Ja" fährt history.go(delta) denselben
 * Sprung noch einmal, sodass der Verlauf identisch mit einer nie angehaltenen
 * Navigation bleibt. Es entstehen keine Zusatz-Einträge und es verschwinden keine.
 */
export function useRoute(): readonly [
  Route,
  (ziel: Route, ersetzen?: boolean) => void,
  (frage: VerlassenWaechter) => () => void,
] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  const waechter = useRef<VerlassenWaechter | null>(null);
  /** Eintragsnummer des Eintrags, auf dem die Anwendung gerade steht. */
  const eintrag = useRef<number>(leseEintragsNummer(window.history.state) ?? 0);
  /** Sprungweite eines gerade rückgängig gemachten popstate — die Frage folgt danach. */
  const schwebt = useRef<number | null>(null);
  /** Der nächste popstate ist die vom Wächter freigegebene Wiederholung des Sprungs. */
  const freigegeben = useRef(false);

  // Für den Notbehelf unten (Eintrag ohne Stempel) — die aktuelle Route ohne
  // Effekt-Neuverdrahtung erreichbar halten.
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    // Den Starteintrag stempeln und die Adresse in ihre kanonische Form bringen
    // (z. B. /kunden/<id>/erfunden → /kunden/<id>). Bewusst replaceState: Das
    // Zurückfallen darf keinen zusätzlichen Verlaufseintrag erzeugen, den die
    // Zurück-Taste dann abarbeiten müsste.
    const start = parseRoute(window.location.pathname);
    if (leseEintragsNummer(window.history.state) === null) {
      const kanonisch =
        start.name === "unbekannt" ? window.location.pathname : routeToPath(start);
      window.history.replaceState({ kiEintrag: 0 }, "", kanonisch);
      eintrag.current = 0;
    }

    function beiPopstate(e: PopStateEvent): void {
      const zielNummer = leseEintragsNummer(e.state);

      if (schwebt.current !== null) {
        // Der kompensierende Sprung ist angekommen — Adresse und Zeiger stehen wieder
        // auf dem Ausgangspunkt. Jetzt (und erst jetzt) den Wächter fragen.
        const delta = schwebt.current;
        schwebt.current = null;
        const frage = waechter.current;
        if (frage === null) return; // Wächter inzwischen abgemeldet: einfach stehen bleiben.
        void Promise.resolve(frage()).then((weiter) => {
          if (!weiter) return;
          freigegeben.current = true;
          window.history.go(delta);
        });
        return;
      }

      if (waechter.current === null || freigegeben.current) {
        freigegeben.current = false;
        if (zielNummer !== null) eintrag.current = zielNummer;
        setRoute(parseRoute(window.location.pathname));
        return;
      }

      if (zielNummer === null) {
        // Eintrag ohne Stempel — kommt im Normalbetrieb nicht vor (alle eigenen
        // Einträge sind gestempelt). Ohne Sprungweite lässt sich der Sprung nicht
        // exakt rückgängig machen; Notbehelf: erst fragen, bei „Nein" den alten Pfad
        // als neuen Eintrag daraufsetzen. Das ist der einzige (degradierte) Pfad, auf
        // dem ein Zusatz-Eintrag entsteht.
        const alterPfad = routeToPath(routeRef.current);
        void Promise.resolve(waechter.current()).then((weiter) => {
          if (weiter) {
            setRoute(parseRoute(window.location.pathname));
          } else {
            eintrag.current += 1;
            window.history.pushState({ kiEintrag: eintrag.current }, "", alterPfad);
          }
        });
        return;
      }

      const delta = zielNummer - eintrag.current;
      if (delta === 0) {
        setRoute(parseRoute(window.location.pathname));
        return;
      }
      schwebt.current = delta;
      window.history.go(-delta);
    }

    window.addEventListener("popstate", beiPopstate);
    return () => window.removeEventListener("popstate", beiPopstate);
    // Läuft genau einmal; alles Veränderliche lebt in Refs.
  }, []);

  const navigiere = useCallback((ziel: Route, ersetzen = false): void => {
    const pfad = routeToPath(ziel);
    if (pfad === window.location.pathname) return; // schon dort — auch den Wächter nicht behelligen.

    const ausfuehren = (): void => {
      if (ersetzen) {
        window.history.replaceState({ kiEintrag: eintrag.current }, "", pfad);
      } else {
        eintrag.current += 1;
        window.history.pushState({ kiEintrag: eintrag.current }, "", pfad);
      }
      setRoute(ziel);
    };

    const frage = waechter.current;
    if (frage === null) {
      ausfuehren();
      return;
    }
    void Promise.resolve(frage()).then((weiter) => {
      if (weiter) ausfuehren();
    });
  }, []);

  const registriereVerlassenWaechter = useCallback(
    (frage: VerlassenWaechter): (() => void) => {
      waechter.current = frage;
      return () => {
        if (waechter.current === frage) waechter.current = null;
      };
    },
    [],
  );

  return [route, navigiere, registriereVerlassenWaechter] as const;
}
